import { CommonModule, isPlatformBrowser } from '@angular/common';
import { Component, DestroyRef, EventEmitter, inject, Inject, OnInit, Output, PLATFORM_ID } from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LoaderService } from '../../../core/services/loader.service';
import { FlashMessageService } from '../../../shared/components/flash-message/flash-message.service';
import { CompanyPlanModel, FeatureValue } from '../../../models/company.model';
import { MySubscriptionService } from './my-subscription.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { PLAN_FEATURE_CONFIG } from '../../../constants/shared.constants';

@Component({
  selector: 'app-my-subscription',
  imports: [CommonModule, TranslateModule],
  templateUrl: './my-subscription.component.html',
  styleUrl: './my-subscription.component.scss',
  providers: [MySubscriptionService]
})
export class MySubscriptionComponent implements OnInit {

  private readonly destroyRef = inject(DestroyRef);
  @Output() plansLoaded = new EventEmitter<{ plans: CompanyPlanModel[], activePlanUid: string, featureRows: any }>();
  @Output() scrollToPlans = new EventEmitter<void>();

  isBrowser: boolean;
  plans: CompanyPlanModel[] = [];
  activePlanUid: string = '';
  currentPlan?: CompanyPlanModel;
  upgradePlans: CompanyPlanModel[] = [];

  constructor(
    @Inject(PLATFORM_ID) private readonly platformId: any,
    public readonly translate: TranslateService,
    public readonly loaderService: LoaderService,
    private readonly flashMessage: FlashMessageService,
    private readonly subscriptionService: MySubscriptionService
  ) { this.isBrowser = isPlatformBrowser(this.platformId) }

  ngOnInit(): void {
    this.getCompanyPlans();
  }

  getCompanyPlans() {
    this.loaderService.display(true);
    this.subscriptionService.getCompanyPlans().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data) => {
        this.plans = this.setPlanDetails(data);
        this.loadActivePlan();
      },
      error: () => {
        this.loaderService.display(false);
        this.flashMessage.showErrorAlert(this.translate.instant('error-message'));
      }
    });
  }

  loadActivePlan() {
    this.subscriptionService.getActivePlanUid().pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data: any) => {
        this.activePlanUid = data.activePlanUid;
        this.assignPlanSections();
        this.loaderService.display(false);
      },
      error: () => {
        this.loaderService.display(false);
        this.flashMessage.showErrorAlert(this.translate.instant('error-message'));
      }
    });
  }

  assignPlanSections() {
    this.currentPlan = this.plans.find(
      p => p.PlanUid === this.activePlanUid
    );
    this.upgradePlans = this.plans.filter(
      p => p.PlanUid !== this.activePlanUid
    );

    this.plansLoaded.emit({
      plans: this.plans,
      activePlanUid: this.activePlanUid,
      featureRows: this.featureRows
    });
  }

  setPlanDetails(plansData: any[]): CompanyPlanModel[] {
    return plansData.map(p => new CompanyPlanModel({
      PlanName: p.PlanName,
      PlanUid: p.PlanUid,
      PriceLabel: p.IsFree ? 'FREE' : `€${(p.Price as number).toFixed(3)}`,
      IsFree: p.IsFree,
      Features: this.mapAllFeatures(p.Features)
    }));
  }


  mapAllFeatures(features: any): any {
    const mapped: any = {};
    Object.keys(features || {}).forEach(key => {
      mapped[key] = this.mapFeature(features[key]);
    });
    return mapped;
  }

  mapFeature(f: any): FeatureValue {
    if (!f) return false;

    switch (f.Type) {

      case 'boolean':
        return f.Value === 'true';

      case 'limit':
        return { text: 'limit', count: Number(f.Value) };

      case 'period':
        return { text: f.Value };

      case 'countries':
        return { text: 'country_choice', count: Number(f.Value) };

      case 'infinite':
        return 'Unlimited';

      case 'global':
        return 'global';

      case 'text':
        return f.Value;

      default:
        return f.Value;
    }
  }


  renderFeature(value: FeatureValue): any {

    if (value === true) return { type: 'yes' };
    if (value === false) return { type: 'no' };

    if (typeof value === 'string') {
      return { type: 'text', value };
    }

    if (typeof value === 'object') {

      let key = value.text;

      if (value.count !== undefined) {
        if (value.text === 'country_choice') {
          key = value.count === 1
            ? 'country_choice_one'
            : 'country_choice_other';
        }
      }

      return {
        type: 'text',
        value: key,
        count: value.count
      };
    }

    return { type: 'no' };
  }

  featureRows = PLAN_FEATURE_CONFIG.map(cfg => ({
    label: cfg.key,
    render: (plan: CompanyPlanModel) =>
      this.renderDynamicFeature(cfg, plan)
  }));

  renderDynamicFeature(config: any, plan: CompanyPlanModel) {

    const f = plan.Features;
    if (!f || !config) return { type: 'no' };
    const value = f[config.key];
    if (!value) return { type: 'no' };
    if (config.key === 'bim_cad_prod_texture' && value === 'Unlimited') {
      return {
        type: 'text',
        value: 'infinite_bim'
      };
    }

    if (config.key === 'iframe' && value === true) {
      return {
        type: 'text',
        value: 'included'
      };
    }

    switch (config.type) {
      case 'simple':
      case 'boolean':
        return this.renderFeature(value);

      case 'composite':
        return this.renderComposite(config, f);

      default:
        return { type: 'no' };
    }
  }

  renderComposite(config: any, features: any) {
    const mainFeature = features[config.key];

    if (typeof mainFeature === 'string') {
      return {
        type: 'text',
        value: mainFeature
      };
    }

    const credits = this.getCount(features['analytics_credits']);
    const switches = this.getCount(features['analytics_switch']);
    const countries = this.getCount(features['analytics_bim_cad_prod_texture']);

    return {
      type: 'composite',
      countryKey: countries === 1 ? 'country_choice_one' : 'country_choice_other',
      analyticsKey: 'analytics_credits_switch',
      analytics_credits: credits,
      analytics_switch: switches,
      count: countries
    };
  }

  getCardFeatures(plan: CompanyPlanModel) {

    const f = plan.Features;
    const result: any[] = [];

    const analyticsCount = this.getCount(f['analytics_bim_cad_prod_texture']);
    if (plan.PlanName === 'Essential') {
      result.push({
        key: 'cardObjectBimUnlimited',
      }, {
        key: 'cardInsightMainLite',
      }, {
        key: 'cardCompanyPage'
      });

      return result;
    }

    if (plan.PlanName === 'Starter') {
      result.push({
        key: 'cardObjectBimUnlimited',
      }, {
        key: 'insight-special',
        extra: 'cardInsightStarterExtra'
      }, {
        key: 'cardCompanyPage'
      });

      return result;
    }

    result.push({
      key: 'cardObjectBimUnlimited'
    }, {
      key: 'insight-special',
      extra: 'cardInsightFullExtra'
    });

    if (f['analytics_bim_cad_prod_texture'] === 'global') {
      result.push({
        key: 'cardAnalyticsGlobal'
      });
    } else {
      result.push({
        key: analyticsCount == 1 ? 'analytics_country_one' : 'analytics_country_other',
        count: analyticsCount
      });
    }

    return result;
  }

  getCount(value: FeatureValue): number | null {
    if (typeof value === 'object' && value !== null && 'count' in value) {
      return Number(value.count);
    }
    return null;
  }

}
